// 메일 화면의 공통 조회를 한 번만 하도록 묶어 주는 얇은 캐시.
//
// 왜 필요한가:
//   사이드바와 본문 목록이 같은 API 를 각자 부른다. 게다가 메일을 읽거나 옮길 때마다
//   갱신 이벤트가 돌아 또 부른다. 실측하니 화면 한 번 쓰는 동안
//   /mail/folders/ 39회, /mail/counts 35회, /mail/labels/ 28회가 나갔다.
//   목록 조회(6회)보다 곁다리 요청이 10배 많았던 셈이라, 느린 것은 쿼리가 아니라
//   왕복 횟수였다(외부에서는 TLS 왕복까지 붙어 더 커진다).
//
// 무엇을 하는가:
//   1) 같은 키로 동시에 들어온 요청은 **하나로 합친다**(in-flight 공유).
//   2) 아주 짧은 시간(기본 1.5초) 안의 재요청은 방금 받은 값을 그대로 준다.
//      메일함 숫자가 1.5초 늦게 바뀌는 것은 문제가 되지 않지만,
//      같은 값을 수십 번 다시 묻는 것은 확실히 문제다.
//   3) 메일을 보내거나 옮기는 등 값이 실제로 바뀌는 동작 뒤에는 invalidate() 로 즉시 버린다.
//
// 캐시를 오래 들고 있지 않는 이유: 안 읽은 메일 수는 자주 바뀌고, 틀린 숫자를 오래
// 보여 주면 사용자가 화면을 믿지 못하게 된다. 목적은 '묵혀 두기'가 아니라 '중복 제거'다.

import { api } from "../../shared/api/client";

type Entry = { at: number; data: unknown };

const TTL_MS = 1500;
const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();

/** 같은 키의 동시 요청을 합치고, 아주 짧은 시간 동안만 재사용한다. */
export async function cachedGet<T>(key: string, url: string, ttl = TTL_MS): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttl) return hit.data as T;

  const running = inflight.get(key);
  if (running) return running as Promise<T>;

  const p = api
    .get<T>(url)
    .then((r) => {
      cache.set(key, { at: Date.now(), data: r.data });
      return r.data;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, p);
  return p;
}

/**
 * 값이 실제로 바뀐 뒤 호출한다(발송·읽음처리·이동·삭제 등).
 * prefix 를 주면 그것으로 시작하는 키만 버린다.
 */
export function invalidateMailCache(prefix?: string) {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}
